import {
  faCalendarCheck,
  faCalendarDays,
  faCalendarPlus,
  faChevronDown,
  faCloudArrowUp,
  faEllipsisV,
  faEnvelope,
  faKey,
  faMobilePhone,
  faPhone,
  faPlus,
  faRefresh
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext, useMemo, useState } from 'react';

import CardOrderMenu from '@/components/CardOrderMenu';
import CardOrderMenuItems from '@/components/CardOrderMenu/components/CardOrderMenuItems';
import FutureAppointmentRequestForDateModal from '@/components/FutureAppointmentRequestForDateModal';
import PersonNav from '@/components/PersonNav';
import { STICKY_CHROME_CLASS } from '@/constants/layout';
import AppContext from '@/contexts/appContext';
import { useSoftPhone } from '@/contexts/softPhoneContext';
import useMicPermission from '@/hooks/useMicPermission';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import { usePersonRunPostSaveTasks } from '@/hooks/useRunPostSaveTasks/useRunPostSaveTasks';
import useSendAppInvite from '@/hooks/useSendAppInvite';
import useSendOneTimeLoginEmail from '@/hooks/useSendOneTimeLoginEmail';
import useStartCall from '@/hooks/useStartCall';
import { useSyncPersonWithHealthie } from '@/hooks/useSyncWithHealthie/useSyncWithHealthie';
import {
  Button,
  buttonVariants,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  dropdownMenuVariants,
  DropdownMenuTrigger,
  Tooltip
} from '@/ui';
import { cn } from '@/ui/lib/utils';
import { reloadPage } from '@/utils/location';
import { APPOINTMENTS_BOOKING_UI, hasPermission } from '@/utils/userPermission';
import usFormatPhoneNumber from '@/utils/usFormatPhoneNumber';

import type { Props } from './types';

const SYNC_UPDATES_COOLDOWN_MILLISECONDS = 60000;

// The toolbar sizes itself against the content column it sits in (see the
// `@container` on its row in Person.tsx), not the window: a wide screen still
// leaves this column narrow once the rails are out and in embedded views.
//
// Below 32rem the actions that do not fit become items in the ellipsis menu,
// which is why the menu itself is hidden above that width -- overflow is all
// it holds. Labels wait for 56rem: seven of them side by side need most of
// that, and the column gives up its width to the rails long before the window
// does.
const OVERFLOW_BUTTON_CLASSNAME = 'hidden @lg:inline-flex';

const PersonToolbar = ({ hideCardOrderMenu = false, navDefaultOptionLabel, person }: Props) => {
  const { host, permissions } = useContext(AppContext);
  const { navigateToBooking, navigateToNewTask, navigateToSchedulingWithId } = useNavigateToPage();
  const canBookAppointment = hasPermission({
    requiredPermission: APPOINTMENTS_BOOKING_UI,
    permissions
  });
  const { itemIcon: itemIconStyles } = dropdownMenuVariants();
  const [isSyncUpdatesCooldown, setIsSyncUpdatesCooldown] = useState(false);
  const [isFutureAppointmentRequestModalOpen, setIsFutureAppointmentRequestModalOpen] =
    useState(false);

  const { isFetching: isSendingAppInvite, refetch: sendAppInvite } = useSendAppInvite({
    personId: person.id,
    enabled: false
  });

  const { isFetching: isSendingOneTimeLoginEmail, refetch: sendOneTimeLoginEmail } =
    useSendOneTimeLoginEmail({
      personId: person.id,
      enabled: false
    });

  const { isFetching: isSendingRunPostSaveTasks, refetch: sendRunPostSaveTasks } =
    usePersonRunPostSaveTasks(person.id, false);

  const { isFetching: isSendingSyncPersonWithHealthie, refetch: syncPersonWithHealthie } =
    useSyncPersonWithHealthie({
      personId: person.id,
      enabled: false
    });

  const handleSyncUpdates = async () => {
    if (isSyncUpdatesCooldown) return;
    setIsSyncUpdatesCooldown(true);

    try {
      await Promise.all([sendRunPostSaveTasks(), syncPersonWithHealthie()]);
    } finally {
      setTimeout(() => setIsSyncUpdatesCooldown(false), SYNC_UPDATES_COOLDOWN_MILLISECONDS);
    }
  };

  const disableSyncUpdates =
    isSendingRunPostSaveTasks || isSendingSyncPersonWithHealthie || isSyncUpdatesCooldown;

  const isSendAppInviteDisabled = useMemo(
    () => isSendingAppInvite || !person.default_phone || !person.settings.channel_sms_enabled,
    [isSendingAppInvite, person.default_phone, person.settings.channel_sms_enabled]
  );

  const sendAppInviteAttributes = useMemo(
    () => ({
      disabled: isSendAppInviteDisabled,
      onClick: () =>
        confirm('Are you sure you want to send an SMS app invite?') ? sendAppInvite() : null,
      title: isSendAppInviteDisabled
        ? !person.default_phone
          ? 'Cannot send app invite. No default phone'
          : 'Cannot send app invite. SMS channel is disabled'
        : 'Send App Invite'
    }),
    [isSendAppInviteDisabled, person.default_phone, sendAppInvite]
  );

  const isSendOneTimeLoginEmailDisabled = useMemo(
    () => isSendingOneTimeLoginEmail || !person.default_email,
    [isSendingOneTimeLoginEmail, person.default_email]
  );

  const sendOneTimeLoginEmailAttributes = useMemo(
    () => ({
      disabled: isSendOneTimeLoginEmailDisabled,
      onClick: () =>
        confirm('Are you sure you want to send a one time login email?')
          ? sendOneTimeLoginEmail()
          : null,
      title: isSendOneTimeLoginEmailDisabled
        ? 'Cannot send email. No default email'
        : 'Send One Time Login Email'
    }),
    [isSendOneTimeLoginEmailDisabled, sendOneTimeLoginEmail]
  );

  const isResetPasswordDisabled = useMemo(() => !person.user?.id, [person.user?.id]);
  const resetPasswordAttributes = useMemo(
    () => ({
      disabled: isResetPasswordDisabled,
      href: person.user?.id ? `${host}/user/${person.user.id}/user_set_password` : undefined,
      title: isResetPasswordDisabled
        ? 'Cannot reset password due to missing user'
        : 'Reset Password'
    }),
    [host, isResetPasswordDisabled, person.user?.id]
  );

  const softPhone = useSoftPhone();
  const startCall = useStartCall(person.id);
  const { micNotApproved, micMessage } = useMicPermission();
  const formattedPhone = usFormatPhoneNumber(person.default_phone);
  const isCallDisabled =
    softPhone === 'disabled-in-embed' || !person.default_phone || micNotApproved;
  const callTooltip =
    softPhone === 'disabled-in-embed'
      ? 'Phone dialer is not supported in this view'
      : !person.default_phone
        ? 'Cannot call. No default phone'
        : micNotApproved
          ? micMessage
          : `Call ${formattedPhone}`;

  const handleCall = () => {
    if (person.default_phone && confirm(`Are you sure you want to call ${formattedPhone}?`)) {
      startCall(person.default_phone);
    }
  };

  return (
    <div
      data-sticky-chrome
      className={`@container flex flex-row flex-wrap space-y-1 items-center justify-between mt-1 mx-auto pt-2 max-w-7xl xs:px-2 sm:px-4 lg:px-6 z-20 bg-gray-100 ${STICKY_CHROME_CLASS.personToolbar}`}>
      {/* The person rail covers this navigation, so only show the select on narrow views. */}
      <div className="lg:hidden">
        <PersonNav defaultOptionLabel={navDefaultOptionLabel} />
      </div>
      <div className="flex items-center z-10">
        <Button
          className="rounded-r-none"
          color="secondary"
          onClick={() => navigateToNewTask(person.id)}
          size="xs"
          title="Add Task">
          <FontAwesomeIcon className="mr-1" icon={faPlus} />
          Task
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger aria-label="Schedule" className="group">
            <Button className="rounded-none" color="secondary" size="xs">
              <FontAwesomeIcon icon={faCalendarDays} className="mr-0 @4xl:mr-2" />
              <span className="hidden @4xl:block">Schedule</span>
              <FontAwesomeIcon
                icon={faChevronDown}
                className="!hidden @4xl:!inline-block ml-1.5 h-3 w-3 transition-transform duration-200 group-data-[open]:rotate-180"
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            {canBookAppointment && (
              <DropdownMenuItem icon={faCalendarCheck} onClick={() => navigateToBooking(person.id)}>
                Book an Appointment
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              icon={faCalendarPlus}
              onClick={() => setIsFutureAppointmentRequestModalOpen(true)}>
              Submit Future Appointment Request
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {softPhone !== null && (
          <Tooltip content={callTooltip}>
            <Button
              aria-label="call"
              className="rounded-none text-white [--btn-bg:var(--color-green-600)] [--btn-hover-bg:var(--color-green-700)]"
              disabled={isCallDisabled}
              onClick={handleCall}
              size="xs">
              <FontAwesomeIcon icon={faPhone} className="mr-0 @4xl:mr-2" />
              <span className="hidden @4xl:block">Call</span>
            </Button>
          </Tooltip>
        )}

        <Tooltip
          content={`Please wait ${SYNC_UPDATES_COOLDOWN_MILLISECONDS / 1000} seconds between syncs`}
          disabled={!isSyncUpdatesCooldown}>
          <Button
            aria-label="sync updates"
            className="rounded-none"
            color="secondary"
            disabled={disableSyncUpdates}
            onClick={() => handleSyncUpdates()}
            size="xs"
            title="Sync Updates">
            <FontAwesomeIcon icon={faCloudArrowUp} className="mr-0 @4xl:mr-2" />
            <span className="hidden @4xl:block">Sync Updates</span>
          </Button>
        </Tooltip>

        <Button
          aria-label="send app invite"
          className={cn(OVERFLOW_BUTTON_CLASSNAME, 'rounded-none')}
          color="neutral"
          disabled={sendAppInviteAttributes.disabled}
          onClick={sendAppInviteAttributes.onClick}
          size="xs"
          title={sendAppInviteAttributes.title}
          variant="outline">
          <FontAwesomeIcon icon={faMobilePhone} className="mr-0 @4xl:mr-2" />
          <span className="hidden @4xl:block">Send App Invite</span>
        </Button>

        <Button
          aria-label="send one time login email"
          className={cn(OVERFLOW_BUTTON_CLASSNAME, 'rounded-none')}
          color="neutral"
          disabled={sendOneTimeLoginEmailAttributes.disabled}
          onClick={sendOneTimeLoginEmailAttributes.onClick}
          size="xs"
          title={sendOneTimeLoginEmailAttributes.title}
          variant="outline">
          <FontAwesomeIcon icon={faEnvelope} className="mr-0 @4xl:mr-2" />
          <span className="hidden @4xl:block">Send Login Email</span>
        </Button>

        {resetPasswordAttributes.href ? (
          <a
            aria-label="reset password"
            className={cn(
              buttonVariants({ variant: 'outline', color: 'neutral', size: 'xs' }),
              OVERFLOW_BUTTON_CLASSNAME,
              'rounded-none'
            )}
            href={resetPasswordAttributes.href}
            rel="noreferrer"
            target="_blank"
            title={resetPasswordAttributes.title}>
            <FontAwesomeIcon icon={faKey} className="mr-0 @4xl:mr-2" />
            <span className="hidden @4xl:block">Reset Password</span>
          </a>
        ) : (
          <Button
            aria-label="reset password"
            className={cn(OVERFLOW_BUTTON_CLASSNAME, 'rounded-none')}
            color="neutral"
            disabled
            title={resetPasswordAttributes.title}
            variant="outline">
            <FontAwesomeIcon icon={faKey} className="mr-0 @4xl:mr-2" />
            <span className="hidden @4xl:block">Reset Password</span>
          </Button>
        )}

        <Button
          aria-label="refresh profile"
          className="rounded-none @lg:rounded-r-md"
          color="neutral"
          onClick={() => reloadPage()}
          size="xs"
          title="Refresh Profile"
          variant="outline">
          <FontAwesomeIcon icon={faRefresh} className="mr-0 @4xl:mr-2" />
          <span className="hidden @4xl:block">Refresh</span>
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger aria-label="Open dropdown" className="@lg:hidden">
            <Button className="rounded-l-none" color="neutral" size="xs" variant="outline">
              <FontAwesomeIcon icon={faEllipsisV} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              disabled={sendAppInviteAttributes.disabled}
              icon={faMobilePhone}
              onClick={sendAppInviteAttributes.onClick}>
              Send App Invite
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={sendOneTimeLoginEmailAttributes.disabled}
              icon={faEnvelope}
              onClick={sendOneTimeLoginEmailAttributes.onClick}>
              Send Login Email
            </DropdownMenuItem>
            <DropdownMenuItem as="div" disabled={resetPasswordAttributes.disabled}>
              <a
                aria-disabled={resetPasswordAttributes.disabled}
                className="flex w-full items-center gap-2.5"
                href={resetPasswordAttributes.href}
                rel="noreferrer"
                target="_blank">
                <FontAwesomeIcon icon={faKey} className={itemIconStyles()} />
                Reset Password
              </a>
            </DropdownMenuItem>

            {!hideCardOrderMenu && (
              <>
                <DropdownMenuSeparator />

                <CardOrderMenuItems />
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Set apart from the person actions on the far right: it acts on the
          page, not on the member. */}
        {!hideCardOrderMenu && <CardOrderMenu className={cn(OVERFLOW_BUTTON_CLASSNAME, 'ml-2')} />}

        <FutureAppointmentRequestForDateModal
          onClose={() => setIsFutureAppointmentRequestModalOpen(false)}
          onSuccess={(appointmentTypeId) =>
            navigateToSchedulingWithId(person.id, new URLSearchParams({ appointmentTypeId }))
          }
          open={isFutureAppointmentRequestModalOpen}
          person={person}
        />
      </div>
    </div>
  );
};
export default React.memo(PersonToolbar);
